import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const parseDateWithOffset = (dateString: string) => {
  return dayjs(dateString);
};

export const formatDateWithOffset = (date: any) => {
  return dayjs(date).format(); // Default ISO 8601 format with offset
};

export const getUtcDate = (date: any) => {
  return dayjs(date).utc().format();
};

export const getLocalDate = (date: any) => {
  return dayjs(date).local().format();
};

export const getSpecificTimeZoneDate = (date: any, timeZone: string) => {
  return dayjs(date).tz(timeZone).format();
};

export const serviceOrderChangeDateParser = (dateTimeRange: string) => {
  if (typeof dateTimeRange !== 'string') {
    return undefined;
  }
  // Regular expression to match the expected format
  const dateTimeRangeRegex = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2} - \d{2}:\d{2}$/;

  // Check if the input string matches the expected format
  if (!dateTimeRange.match(dateTimeRangeRegex)) {
    return undefined;
  }

  const [datePart, fromTime, _spliter, toTime] = dateTimeRange.split(' ');

  // Combine date part with from and to times to create full date-time strings
  const fromDateTimeStr = `${datePart} ${fromTime}`;
  const toDateTimeStr = `${datePart} ${toTime}`;

  // Parse the date and time parts as UTC
  const fromDateTime = dayjs.utc(fromDateTimeStr, 'DD/MM/YYYY HH:mm');
  const toDateTime = dayjs.utc(toDateTimeStr, 'DD/MM/YYYY HH:mm');

  // Convert to local timezone
  const fromDateTimeLocal = fromDateTime.local();
  const toDateTimeLocal = toDateTime.local();

  // Additional validation to ensure the parsed dates are valid
  if (!fromDateTimeLocal.isValid() || !toDateTimeLocal.isValid()) {
    return undefined;
  }

  // Combine date and time into the desired format
  const combinedString = `${fromDateTimeLocal.format('DD/MM/YYYY')} ${fromDateTimeLocal.format('HH:mm')} - ${toDateTimeLocal.format('HH:mm')}`;

  return combinedString;
};

export const getDateRangeFromPreset = (range?: string): { startAt?: string; endAt?: string } => {
  if (!range) return {};
  const now = dayjs();
  const fmt = (d: dayjs.Dayjs) => d.toISOString();
  switch (range) {
    case 'today':
      return { startAt: fmt(now.startOf('day')), endAt: fmt(now.endOf('day')) };
    case 'this_week':
      return { startAt: fmt(now.startOf('week')), endAt: fmt(now.endOf('week')) };
    case 'this_month':
      return { startAt: fmt(now.startOf('month')), endAt: fmt(now.endOf('month')) };
    case 'last_month':
      return { startAt: fmt(now.subtract(1, 'month').startOf('month')), endAt: fmt(now.subtract(1, 'month').endOf('month')) };
    default:
      return {};
  }
};

export const roundUpToNext15 = (date: dayjs.Dayjs) => {
  const remainder = 15 - (date.minute() % 15);

  return remainder === 15 ? date.second(0) : date.add(remainder, 'minute').second(0);
};

export interface TimeOption {
  value: string;
  label: string;
}

export const MINUTES_IN_DAY = 24 * 60;

/** '09:30' -> 570 */
export const timeToMinutes = (time: string) => {
  const [hour, minute] = time.split(':').map(Number);
  return hour * 60 + minute;
};

/** 570 -> '09:30' */
export const minutesToTime = (minutes: number) =>
  `${Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}`;

/** Select options for every `stepMinutes` between `fromMinutes` (inclusive) and `toMinutes` (exclusive). */
export const buildTimeOptions = (
  stepMinutes = 15,
  fromMinutes = 0,
  toMinutes = MINUTES_IN_DAY
): TimeOption[] =>
  Array.from({ length: Math.ceil((toMinutes - fromMinutes) / stepMinutes) }, (_, index) =>
    minutesToTime(fromMinutes + index * stepMinutes)
  ).map((time) => ({ value: time, label: time }));

/** Sorts, de-duplicates and formats a list of minute offsets as select options. */
export const toTimeOptions = (minutes: number[]): TimeOption[] =>
  [...new Set(minutes)]
    .sort((a, b) => a - b)
    .map((value) => ({ value: minutesToTime(value), label: minutesToTime(value) }));
